import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { Button } from 'common-src/components/base';
import {
  extractRoutineExam,
  getLastUpdatedPatientRoutineExamDate,
  getMostRecentPatientRoutineExams,
  ROUTINE_EXAMS_DATA_CONFLICT_WARNING_ID,
  ROUTINE_EXAMS_INSTRUCTIONS,
} from 'common-src/models/PatientRoutineExam';
import { getPatientRoutineExamsRequest } from 'common-src/models/PatientRoutineExam/actions';
import { ColorsNew } from 'common-src/styles';

import { RoutineExams } from 'src/components/forms';
import useCustomSelector from 'src/hooks/useCustomSelector';
import useInterval from 'src/hooks/useInterval';

import styles from './RoutineExamsModule.module.scss';

const RoutineExamsModule = ({
  patientId,
  errors,
  routineExams,
  setRoutineExams,
  setHasDetectedChanges,
  routineExamsLastUpdate,
  setRoutineExamsLastUpdate,
}) => {
  const dispatch = useDispatch();

  const patientRoutineExams = useCustomSelector((state) =>
    getMostRecentPatientRoutineExams(state, patientId),
  );
  const lastUpdatedPatientRoutineExamDate = useCustomSelector((state) =>
    getLastUpdatedPatientRoutineExamDate(state, patientId),
  );

  const hasDataConflict =
    lastUpdatedPatientRoutineExamDate &&
    moment(routineExamsLastUpdate).isBefore(moment(lastUpdatedPatientRoutineExamDate));

  useInterval(() => {
    dispatch(getPatientRoutineExamsRequest({ patientId, sort_order: 'desc', limit: 5 }, {}));
  });

  const handleDetectedChanges = () => {
    setHasDetectedChanges(new Date());
    setRoutineExamsLastUpdate(moment().utc().toISOString());
  };

  useEffect(() => {
    if (!routineExamsLastUpdate) {
      setRoutineExams(patientRoutineExams.map(extractRoutineExam));
      handleDetectedChanges();
    }
  }, [routineExamsLastUpdate]);

  const renderDataConflictInstructions = () => {
    if (!hasDataConflict) return null;

    return (
      <div className={['font-s-14', 'm-b-20'].join(' ')}>
        <p
          id={ROUTINE_EXAMS_DATA_CONFLICT_WARNING_ID}
          className={[styles.warning, 'font-s-16', 'm-b-8'].join(' ')}
        >
          Another user has made changes to the exams for this member since your draft was created.
          Please review the changes under the Orders tab.
        </p>
        <p className={['font-w-500', 'm-b-8'].join(' ')}>
          Would you like to accept and apply their changes?
        </p>
        <Button
          classNames={[styles.button, 'm-b-8']}
          backgroundColor="transparent"
          textColor={ColorsNew.darkGreen}
          text="Yes, Accept Changes"
          onClick={() => {
            setRoutineExams(patientRoutineExams.map(extractRoutineExam));
            handleDetectedChanges();
          }}
        />
        <Button
          classNames={[styles.button]}
          backgroundColor="transparent"
          borderColor={ColorsNew.mediumRed}
          textColor={ColorsNew.mediumRed}
          text="No, Keep My Data"
          onClick={() => handleDetectedChanges()}
        />
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <p className={['font-w-600', 'font-s-16', 'm-b-6'].join(' ')}>Routine Diabetes Exams</p>
      <p className={[styles.instructions, 'font-s-14', 'm-b-20'].join(' ')}>
        {ROUTINE_EXAMS_INSTRUCTIONS}
      </p>
      {renderDataConflictInstructions()}
      <RoutineExams
        patientId={patientId}
        isEditMode
        formData={routineExams}
        setFormData={(data) => {
          if (hasDataConflict) return;
          setRoutineExams(data);
          handleDetectedChanges();
        }}
        errors={Object.fromEntries(errors)}
        setErrors={() => {}}
        isEmbedded
        isDisabled={hasDataConflict}
      />
    </div>
  );
};

RoutineExamsModule.propTypes = {
  patientId: PropTypes.number,
  errors: PropTypes.object,
  routineExams: PropTypes.array,
  setRoutineExams: PropTypes.func,
  setHasDetectedChanges: PropTypes.func,
  routineExamsLastUpdate: PropTypes.string,
  setRoutineExamsLastUpdate: PropTypes.func,
};

export default RoutineExamsModule;
